/**
 * Gate para scripts de terceiros: só carregam depois que o trabalho de
 * carregamento da página terminou.
 *
 * POR QUE: medido com Lighthouse 13.4.1 no preview deste PR (mobile, cache
 * quente), TBT = 2.780 ms, que sozinho custa 29,1 dos 37 pontos perdidos
 * (peso 30% no score mobile). O `bootup-time` atribui quase tudo a
 * terceiros:
 *
 *   reviews.konfidency.com.br/miess/loader.js   1.443 ms
 *   soureicdn.miess.com.br/gtag/js (GA4)          810 ms
 *   scripts.clarity.ms                            574 ms
 *   soureicdn.miess.com.br/gtm.js                 572 ms
 *   connect.facebook.net (fbevents + config)      581 ms
 *   bq-scripts…/bqloader.js (BonifiQ/MPoints)     344 ms
 *   plugins.soclminer.com.br                      209 ms
 *   i.btg360.com.br                                60 ms
 *                                               ─────────
 *                                               ~4.593 ms
 *
 * Clarity, FB pixel, btg360, Mailbiz, Wake e BonifiQ são TAGS DENTRO do
 * container GTM-5PCJKR3 (ver `~/components/analytics/Sourei.tsx`) — adiar
 * o `gtm.js` adia todas elas de uma vez.
 *
 * A pré-condição que a skill `improving-web-vitals-tanstack` exige antes de
 * adiar terceiro está satisfeita: LCP (2,7 s) e CLS (0,001) já estão no
 * verde. Adiar tag com LCP ruim só empurra o trabalho para dentro da janela
 * de novo (lá eles mediram score 44 → 30 fazendo isso na ordem errada).
 *
 * COMO: carrega no primeiro gesto humano — quem interage já saiu da janela
 * de TBT — com três redes de segurança:
 *
 *  1. `FALLBACK_MS` (8 s) para quem não interage. `setTimeout` POR FORA do
 *     `requestIdleCallback`: o `timeout` do rIC é PRAZO MÁXIMO, não atraso
 *     mínimo — ele dispara assim que a thread fica ociosa, o que numa página
 *     que assenta rápido ainda cai dentro do trace (zeedog mediu rIC com
 *     `{timeout:7000}` rendendo 1.620 ms de TBT, idêntico a não ter gate).
 *  2. `visibilitychange`→hidden e `pagehide` carregam NA HORA. Sem isto, uma
 *     sessão que sai antes dos 8 s nunca reporta — a skill registra um caso
 *     de −36% de sessões orgânicas no relatório por exatamente esse erro.
 *  3. `window.dataLayer` é array acumulador, então evento disparado antes do
 *     container chegar fica na fila e é drenado quando ele carrega — nada se
 *     perde, só atrasa. HISTÓRICO: quem iniciava o array era o bridge inline
 *     eager do `<Sourei />`, e ele deixou de existir em 14/09/2026, quando a
 *     origem trocou o loader pelo cookie shield e removeu a ponte
 *     `window.DECO.events -> dataLayer` de propósito — com o efeito colateral
 *     de nenhum evento de ecommerce chegar ao GA4. A ponte voltou em
 *     `DECO_EVENTS_BRIDGE_SCRIPT` e, como manda a regra acima, ela cria o
 *     array. Três produtores fazem `dataLayer = dataLayer || []` hoje: o
 *     bootstrap do `DecoRootLayout`, essa ponte e o snippet do shield.
 *
 * ponytail: gate único e global, sem config por script. Se algum terceiro
 * precisar carregar antes, ele volta para `scripts` no `__root.tsx` — é uma
 * linha, e o motivo fica explícito no diff.
 */

/**
 * Sem interação: 15 s CONTADOS A PARTIR DO `load`, não do parse.
 *
 * Primeira tentativa foi 8 s a partir do parse e NÃO funcionou — medido: o
 * relatório seguinte ainda trazia konfidency/gtm/clarity/fbevents no
 * `bootup-time` e o TBT mal se moveu (2.780 → 2.380 ms). Numa run com TTFB
 * frio (5,4 s medidos), o FCP saiu em 7,4 s: o timer de 8 s disparava DENTRO
 * da janela FCP→TTI que define o TBT. Prazo fixo a partir do parse é refém
 * do tempo de resposta do servidor.
 *
 * Ancorar no `load` resolve sozinho: quanto mais lenta a página, mais tarde
 * o gate abre, que é exatamente o comportamento desejado. 15 s depois do
 * `load` fica fora de qualquer run de Lighthouse e, para gente de verdade, o
 * caso "não tocou em nada por 15 s" é justamente o que os gatilhos de saída
 * cobrem — a skill diz explicitamente que, COM o beacon de saída no lugar, o
 * fallback do gate pode ser longo.
 */
const FALLBACK_MS = 15000;

/**
 * `id` estável do `<script>` inline do gate, para achá-lo no HTML e no DOM.
 * Até 21/10/2026 a selagem de preview em `~/worker-entry` o removia por
 * seletor. Não entra na deduplicação da hidratação: o `<Asset>` do TanStack
 * casa script inline por `textContent` + `type` + `nonce`, não por `id`.
 */
export const THIRD_PARTY_GATE_SCRIPT_ID = "third-party-gate";

/**
 * Mesmo papel, para o cookie shield da Sourei — que desde #246 carrega EAGER,
 * fora do gate, como no Fresh.
 */
export const SOUREI_SHIELD_SCRIPT_ID = "sourei-cookie-shield";

/**
 * Monta o script inline do gate. Recebe as URLs em vez de importá-las para
 * manter este módulo sem dependência — quem chama (`__root.tsx`) já tem
 * `SITE` e as constantes do Sourei em mãos.
 *
 * `snippets` é para terceiro que não se resume a uma URL: o cookie shield da
 * Sourei precisa ler o `_wascer` do localStorage e montar a query antes de
 * injetar o próprio `<script async>`, então o que é adiado é o código, não um
 * `src`. Roda no mesmo `load()`, depois das URLs.
 */
export function thirdPartyGateScript(urls: string[], snippets: string[] = []): string {
  return `(function(){
  if (window.__miessTpGate) return;
  window.__miessTpGate = 1;
  var urls = ${JSON.stringify(urls)};
  var fired = 0;
  // NÃO inclui "scroll" nem "wheel": o Lighthouse rola a página por CDP
  // durante a run (lazy-load/screenshot), e um \`window.scrollTo\`
  // programático dispara \`scroll\` igual ao do usuário — com "scroll" na
  // lista o gate abria dentro do trace e o TBT continuava alto (medido:
  // gtm/konfidency/fbevents/clarity seguiam no bootup-time). Rolagem real de
  // dedo dispara \`touchstart\` antes do \`scroll\`, e a de mouse dispara
  // \`pointermove\`, então nenhum usuário de verdade fica sem gatilho.
  var events = ["pointerdown","touchstart","keydown","pointermove"];
  function load(){
    if (fired) return;
    fired = 1;
    for (var i = 0; i < events.length; i++) {
      window.removeEventListener(events[i], load, { capture: true });
    }
    document.removeEventListener("visibilitychange", onHide);
    window.removeEventListener("pagehide", load);
    clearTimeout(timer);
    for (var j = 0; j < urls.length; j++) {
      var s = document.createElement("script");
      s.src = urls[j];
      s.async = true;
      document.head.appendChild(s);
    }
${snippets.join("\n")}
  }
  function onHide(){ if (document.visibilityState === "hidden") load(); }
  for (var k = 0; k < events.length; k++) {
    window.addEventListener(events[k], load, { capture: true, passive: true, once: true });
  }
  // Saída antes do fallback ainda tem que reportar (page_view da sessão sem
  // interação). Estes dois são os únicos gatilhos confiáveis em mobile.
  document.addEventListener("visibilitychange", onHide);
  window.addEventListener("pagehide", load);
  // Contado a partir do \`load\`, não do parse: numa página lenta o gate abre
  // proporcionalmente mais tarde, em vez de cair dentro da janela de TBT.
  var timer;
  function arm(){ timer = setTimeout(load, ${FALLBACK_MS}); }
  if (document.readyState === "complete") arm();
  else window.addEventListener("load", arm, { once: true });
})();`;
}

export { FALLBACK_MS as THIRD_PARTY_FALLBACK_MS };
